import React, { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, ExternalLink, Image as ImageIcon, X } from 'lucide-react';
import { formatDateTime } from '../lib/format';

/*
 * PhotoLightbox.jsx ? Read-only viewer for equipment photos.
 * Pops up from an icon button on the Equipment list row. Pagination via arrows
 * or ?/? keys. Closes with Esc or clicking the backdrop.
 */
export const PhotoLightbox = ({ open, onClose, vehicle, photos = [] }) => {
  // Only show approved photos in the public viewer (pending/rejected hidden).
  const visible = (photos || []).filter((p) => p.status === 'approved');
  const [idx, setIdx] = useState(0);

  const safeIdx = Math.min(idx, Math.max(visible.length - 1, 0));
  const photo = visible[safeIdx];

  const prev = useCallback(() => { setIdx((i) => (i - 1 + visible.length) % visible.length); }, [visible.length]);
  const next = useCallback(() => { setIdx((i) => (i + 1) % visible.length); }, [visible.length]);

  useEffect(() => {
    if (!open) return;
    setIdx(0);
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, prev, next, onClose]);

  if (!vehicle) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden bg-slate-900 text-white border-slate-800">
        <DialogTitle className="sr-only">Equipment photos for {vehicle.equipment_code}</DialogTitle>
        <DialogDescription className="sr-only">Photo viewer</DialogDescription>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-700">
          <div className="min-w-0">
            <div className="font-mono text-sm font-semibold truncate">{vehicle.equipment_code}</div>
            <div className="text-[11px] text-slate-400 truncate">{vehicle.description}</div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="bg-slate-700 text-slate-200 hover:bg-slate-700">{visible.length > 0 ? `${safeIdx + 1} / ${visible.length}` : '0'}</Badge>
            <Button size="sm" variant="ghost" onClick={onClose} className="text-slate-200 hover:bg-slate-700 h-7 w-7 p-0" data-testid="photo-lightbox-close">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {visible.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <ImageIcon className="h-10 w-10 mx-auto opacity-50" />
            <div className="mt-2 text-sm">No approved photos for this equipment yet.</div>
            <div className="text-[11px] mt-1">Pending or rejected photos are only visible in the Location &amp; Photos manager.</div>
          </div>
        ) : (
          <div className="relative" data-testid="photo-lightbox-body">
            <div className="bg-black grid place-items-center min-h-[300px] max-h-[70vh] overflow-hidden">
              <img
                src={photo.image_base64}
                alt={photo.caption || vehicle.equipment_code}
                className="max-h-[70vh] max-w-full object-contain"
                data-testid="photo-lightbox-img"
              />
            </div>

            {/* Prev / Next */}
            {visible.length > 1 && (
              <>
                <button
                  onClick={prev}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/70 text-white rounded-full p-2 transition"
                  aria-label="Previous photo"
                  data-testid="photo-lightbox-prev"
                ><ChevronLeft className="h-5 w-5" /></button>
                <button
                  onClick={next}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/70 text-white rounded-full p-2 transition"
                  aria-label="Next photo"
                  data-testid="photo-lightbox-next"
                ><ChevronRight className="h-5 w-5" /></button>
              </>
            )}

            {/* Caption / meta */}
            <div className="px-4 py-3 bg-slate-800 border-t border-slate-700">
              {photo.caption ? <div className="text-sm text-slate-100">{photo.caption}</div> : <div className="text-sm text-slate-400 italic">No caption</div>}
              <div className="flex flex-wrap items-center gap-x-3 text-[11px] text-slate-400 mt-1">
                <span>— {photo.uploaded_by_name || 'unknown'}</span>
                <span>— {formatDateTime(photo.uploaded_at)}</span>
                {photo.size_kb && <span>— {photo.size_kb} KB</span>}
                <a
                  href={photo.image_base64}
                  download={`${vehicle.equipment_code}-${photo.id}.jpg`}
                  className="ml-auto inline-flex items-center text-slate-300 hover:text-white"
                ><ExternalLink className="h-3 w-3 mr-1" /> Download</a>
              </div>
            </div>

            {/* Thumbnail strip */}
            {visible.length > 1 && (
              <div className="flex gap-1.5 overflow-x-auto px-3 py-2 bg-slate-900 border-t border-slate-700">
                {visible.map((p, i) => (
                  <button
                    key={p.id}
                    onClick={() => setIdx(i)}
                    className={`flex-shrink-0 border-2 rounded ${i === safeIdx ? 'border-blue-400' : 'border-transparent opacity-60 hover:opacity-100'}`}
                    aria-label={`Photo ${i + 1}`}
                    data-testid={`photo-lightbox-thumb-${i}`}
                  >
                    <img src={p.image_base64} alt="" className="h-12 w-16 object-cover rounded-sm" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PhotoLightbox;
